import { LazyLoadImage } from "react-lazy-load-image-component";
import { ExperienceItem } from "@/global";
import { LinkButton } from "@/components/shared/LinkButton/LinkButton";
import { SubHeading } from "@/components/shared/SubHeading/SubHeading";
import { TechList } from "@/components/shared/TechList/TechList";
import { useTheme } from "../../../../contexts/ThemeContext";
import { Separator } from "@/components/shared/Separator/Separator";
import { Badge } from "@/components/shared/Badge/Badge";
import { SkillBadge } from "@/components/shared/SkillBadge/SkillBadge";
import { MetadataText } from "@/components/shared/MetadataText/MetadataText";
import { useStackscanTechs } from "@/hooks/useStackscanTechs";

export const ExperienceCard = ({
    experience,
}: {
    experience: ExperienceItem;
}) => {
    const { theme } = useTheme();
    // Destructure properties from experience
    const { 
        title, 
        company, 
        location, 
        date, 
        description, 
        details, 
        skills, 
        link,
        additionalLinks,
        logo, 
        type, 
        isCurrent 
    } = experience;

    const logoSrc = typeof logo === "string"
        ? logo
        : theme === "dark"
            ? logo.darkImage
            : logo.lightImage;

    const techsForDisplay = useStackscanTechs(experience);

    return (
        <div
            className="w-[90%] md:w-[48%] grid gap-4 text-slate-700 dark:text-white text-left border border-zinc-500 rounded-lg transition-all duration-300 hover:border-portfolio-green p-4"
            style={{ gridTemplateRows: 'auto auto auto auto' }}
        >
            {/* Header - 20% */}
            <div className="w-full flex flex-row justify-between items-start overflow-hidden">
                <div className="flex flex-row items-center gap-6">
                    <LazyLoadImage
                        className="h-12 w-12 rounded object-contain"
                        src={logoSrc}
                        alt={company}
                        loading="lazy"
                    />
                    <div className="flex flex-col gap-2 flex-1">
                        <SubHeading text={`${title} @ ${company}`} />
                        <div className="flex flex-row items-center gap-4 flex-wrap">
                            <MetadataText>{date}</MetadataText>
                            <Separator />
                            <MetadataText>{type}</MetadataText>
                            <Separator />
                            <MetadataText>{location}</MetadataText>
                            {isCurrent && (
                                <>
                                    <Separator />
                                    <Badge text="Current" variant="default" />
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex flex-row items-center gap-2">
                    {additionalLinks && additionalLinks.map((al, i) => (
                        <a
                            key={i}
                            href={al.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center"
                        >
                            <img
                                src={al.logo}
                                alt={al.alt}
                                className={`h-6 w-auto object-contain${al.className ? ` ${al.className}` : ""}`}
                            />
                        </a>
                    ))}
                    {link && <LinkButton link={link} />}
                </div>
            </div>

            {/* Description - 10% */}
            <div className="w-full overflow-hidden">
                <p className="leading-relaxed m-0 text-sm text-slate-700 dark:text-portfolio-white">{description}</p>
            </div>

            {/* Details/Bullet Points - 50% */}
            <div className="w-full flex items-start">
                {details && details.length > 0 && (
                    <ul className="m-0 pl-0 list-none text-sm text-slate-700 dark:text-portfolio-white">
                        {details.map((detail) => (
                            <li key={detail.key} className="mb-1.5 leading-snug last:mb-0">
                                ✅ {detail.text}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Tech Stack - 20% */}
            {(techsForDisplay && techsForDisplay.length > 0) || (skills && skills.length > 0) ? (
                <div className="w-full h-full flex items-center">
                    <div className="w-full flex items-center justify-between">
                        {techsForDisplay && techsForDisplay.length > 0 && (
                            <TechList techs={techsForDisplay} />
                        )}
                        {skills && skills.length > 0 && (
                            <div className="flex flex-wrap gap-2 items-center self-end pb-2">
                                {skills.map((skill, index) => (
                                    <SkillBadge key={index} skill={skill} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
};
